import {gql} from '@apollo/client';
import {StyleSheet, css} from 'aphrodite';
import React from 'react';

import {
  ChemicalStructureDrawer,
  ChemicalStructureDrawerMode,
  ChemicalStructureDrawerValue,
} from 'chemistry/marvinjs-wrappers/ChemicalStructureDrawer';
import {isError} from 'graphql-client/Error';
import {ComplexPolymerType} from 'modbio/complex-polymers/ComplexPolymerConstants';
import {
  ConcatenateMolfilesQuery,
  ConcatenateMolfilesQueryVariables,
} from 'modbio/complex-polymers/tabs/__generated/ConcatenateMolfilesQuery';
import {
  AttachConjugateState,
  ConnectionSideType,
  SimplePolymer,
} from 'modbio/complex-polymers/tabs/ComplexPolymerAttachConjugateModalUtil';
import {ComponentRow} from 'modbio/complex-polymers/tabs/ComplexPolymerAttachConjugateSelectMenus';
import {
  ConjugateConnection,
  ConjugateConnectionMap,
} from 'modbio/complex-polymers/tabs/ComplexPolymerComponentsSummaryUtil';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {GRAY_LIGHT} from 'ui-library/Colors';

type MolfileOrError = ConcatenateMolfilesQuery['concatenateMolfiles']['molfile'];

export const CONCATENATE_MOLFILES_QUERY = gql`
  query ConcatenateMolfilesQuery($monomerOrMoleculeIds: [StrictString!]!, $molfiles: [StrictString!])
  @engComponent(name: MODIFIED_BIOLOGICS)
  @skipAudit {
    concatenateMolfiles(monomerOrMoleculeIds: $monomerOrMoleculeIds, molfiles: $molfiles) {
      id
      molfile {
        ... on StringPropertyNode {
          value
        }
        ... on ErrorNode {
          message
        }
      }
    }
  }
`;

export function canRunConcatenateMolfilesQuery(variables: ConcatenateMolfilesQueryVariables): boolean {
  // Empty variables will happen if the user is making a connection between two nucleotide sequences, but has
  // not selected a residue index/monomer on either of them yet.
  return variables.monomerOrMoleculeIds.length > 0 || (variables.molfiles?.length ?? 0) > 0;
}

interface AttachmentDrawerProps {
  attachmentMolfile: ChemicalStructureDrawerValue;
  setAttachmentMolfile: (molfile: ChemicalStructureDrawerValue) => void;
}

function AttachmentDrawer({attachmentMolfile, setAttachmentMolfile}: AttachmentDrawerProps): JSX.Element {
  return (
    <ChemicalStructureDrawer
      initialValue={attachmentMolfile}
      onMoleculeChange={setAttachmentMolfile}
      mode={ChemicalStructureDrawerMode.STANDARD}
    />
  );
}

interface DrawnStyleAttachConjugateSectionProps {
  complexPolymerId?: string;
  complexPolymerType: ComplexPolymerType;
  isEditMode: boolean;
  conjugateConnectionMap: ConjugateConnectionMap;
  molfileOrError: MolfileOrError;
  forCreate: boolean;
  attachConjugateState: AttachConjugateState;
  onClickAttach?: (conjugateConnection: ConjugateConnection) => void;
  simplePolymers: Array<SimplePolymer>;
}

export default function DrawnStyleAttachConjugateSection(
  props: DrawnStyleAttachConjugateSectionProps
): JSX.Element {
  const {molfileOrError, conjugateConnectionMap, attachConjugateState} = props;
  const {connection, updateConnection, attachmentMolfile, attachmentMolfileError, setAttachmentMolfile} =
    attachConjugateState;

  let drawerComponent = null;
  if (isError(molfileOrError)) {
    drawerComponent = <InlineBanner bannerType={InlineBannerType.ERROR} text={molfileOrError.message} />;
  } else {
    drawerComponent = (
      <AttachmentDrawer
        attachmentMolfile={attachmentMolfile ?? molfileOrError.value}
        setAttachmentMolfile={setAttachmentMolfile}
      />
    );
  }

  const currentConjugateConnection = conjugateConnectionMap[connection.target.polymer_ref];

  return (
    <div className={css(styles.wrapper)}>
      <div className={css(styles.componentSelection)}>
        <ComponentRow
          {...props}
          conjugateConnectionMap={conjugateConnectionMap}
          currentConjugateConnection={currentConjugateConnection}
          connectionSideType={ConnectionSideType.TARGET}
          connection={connection}
          updateConnection={updateConnection}
          attachmentMolfile={attachmentMolfile}
          attachmentMolfileError={attachmentMolfileError}
        />
        <ComponentRow
          {...props}
          conjugateConnectionMap={conjugateConnectionMap}
          currentConjugateConnection={currentConjugateConnection}
          connectionSideType={ConnectionSideType.SOURCE}
          connection={connection}
          updateConnection={updateConnection}
          attachmentMolfile={attachmentMolfile}
          attachmentMolfileError={attachmentMolfileError}
        />
      </div>
      <div className={css(styles.drawer)}>{drawerComponent}</div>
    </div>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    display: 'flex',
    flexGrow: 1,
    alignItems: 'stretch',
    height: '100%',
  },
  componentSelection: {
    display: 'flex',
    flexDirection: 'column',
    width: '30%',
  },
  drawer: {
    display: 'flex',
    borderLeft: `1px solid ${GRAY_LIGHT}`,
    paddingBottom: 5,
    paddingLeft: 2,
    width: '70%',
  },
  attachButton: {
    alignSelf: 'flex-end',
    marginTop: 'auto',
    marginBottom: 10,
    marginRight: 10,
  },
});
